import React, { useState, useEffect, useCallback } from 'react';
import { paperlessApi, vertraegeApi } from '../api';

// ─── Konfiguration ────────────────────────────────────────────────────────────

const RESULT_TYPE_CONFIG = {
  NEW_CONTRACT:                 { label: 'Neuer Vertrag',        color: '#7C3AED', bg: '#F5F3FF', icon: '🆕' },
  EXISTING_CONTRACT_ENRICHMENT: { label: 'Anreicherung',         color: '#059669', bg: '#ECFDF5', icon: '✨' },
  POSSIBLE_CHANGE:              { label: 'Mögliche Änderung',    color: '#D97706', bg: '#FFFBEB', icon: '🔄' },
  REVIEW_REQUIRED:              { label: 'Prüfen erforderlich',  color: '#DC2626', bg: '#FEF2F2', icon: '⚠️' },
  NOT_CONTRACT_RELEVANT:        { label: 'Kein Vertrag',         color: '#6B7280', bg: '#F3F4F6', icon: '📄' },
  ERROR:                        { label: 'Fehler',               color: '#DC2626', bg: '#FEF2F2', icon: '❌' },
};

const STATUS_CONFIG = {
  PENDING:  { label: 'Ausstehend', color: '#D97706', bg: '#FFFBEB' },
  APPLIED:  { label: 'Erledigt',   color: '#059669', bg: '#ECFDF5' },
  IGNORED:  { label: 'Ignoriert',  color: '#6B7280', bg: '#F3F4F6' },
};

const FILTER_TYPES = [
  { value: '', label: 'Alle' },
  { value: 'NEW_CONTRACT', label: 'Neue Verträge' },
  { value: 'EXISTING_CONTRACT_ENRICHMENT', label: 'Anreicherungen' },
  { value: 'POSSIBLE_CHANGE', label: 'Änderungen' },
  { value: 'REVIEW_REQUIRED', label: 'Zu prüfen' },
  { value: 'NOT_CONTRACT_RELEVANT', label: 'Kein Vertrag' },
];

const FILTER_STATUS = [
  { value: 'PENDING', label: 'Ausstehend' },
  { value: '', label: 'Alle' },
  { value: 'APPLIED', label: 'Erledigt' },
  { value: 'IGNORED', label: 'Ignoriert' },
];

// ─── Hilfsfunktionen ──────────────────────────────────────────────────────────

function formatDate(s) {
  if (!s) return '–';
  return new Date(s).toLocaleDateString('de-DE', { dateStyle: 'medium' });
}

function formatDateTime(s) {
  if (!s) return '–';
  return new Date(s).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' });
}

function konfidenzLabel(k) {
  if (k == null) return '–';
  const pct = Math.round(k * 100);
  const color = pct >= 80 ? '#059669' : pct >= 60 ? '#D97706' : '#DC2626';
  return <span style={{ color, fontWeight: 600 }}>{pct}%</span>;
}

// ─── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ label, active, onClick }) {
  return (
    <button onClick={onClick} style={{
      padding: '4px 12px', borderRadius: 20, fontSize: 13, cursor: 'pointer',
      border: active ? '2px solid #6366F1' : '1px solid #CBD5E1',
      background: active ? '#EEF2FF' : '#F8FAFC',
      color: active ? '#4338CA' : '#475569', fontWeight: active ? 600 : 400,
    }}>{label}</button>
  );
}

// ─── Konfigurations-Modal ─────────────────────────────────────────────────────

function ConfigModal({ onClose, onSaved }) {
  const [baseUrl, setBaseUrl] = useState('');
  const [apiToken, setApiToken] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await paperlessApi.configure({ base_url: baseUrl.trim(), api_token: apiToken.trim() });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card, #fff)', borderRadius: 12, padding: 28, width: 420, maxWidth: '94vw', boxShadow: '0 8px 40px rgba(0,0,0,.18)' }}>
        <h2 style={{ margin: '0 0 16px', fontSize: 18 }}>Paperless konfigurieren</h2>
        <form onSubmit={handleSave}>
          <label style={{ display: 'block', marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#475569' }}>Paperless URL</div>
            <input value={baseUrl} onChange={e => setBaseUrl(e.target.value)} placeholder="https://paperless.example.com"
              required style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 14, boxSizing: 'border-box' }} />
          </label>
          <label style={{ display: 'block', marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4, color: '#475569' }}>API-Token</div>
            <input value={apiToken} onChange={e => setApiToken(e.target.value)} type="password" placeholder="Token aus Paperless-Profil"
              required style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 14, boxSizing: 'border-box' }} />
            <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 4 }}>Token wird verschlüsselt gespeichert und nie zurückgegeben.</div>
          </label>
          {error && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '8px 12px', borderRadius: 6, fontSize: 13, marginBottom: 12 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #CBD5E1', background: 'transparent', cursor: 'pointer' }}>Abbrechen</button>
            <button type="submit" disabled={loading} style={{ padding: '8px 16px', borderRadius: 6, background: '#6366F1', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
              {loading ? 'Prüfe…' : 'Speichern & testen'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Detail-Modal ─────────────────────────────────────────────────────────────

function DetailModal({ resultId, vertraege, onClose, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);

  // For create-contract form
  const [newTitel, setNewTitel] = useState('');
  const [newKatId, setNewKatId] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  // For apply-fields: selected fields
  const [selectedFields, setSelectedFields] = useState({});
  const [linkContractId, setLinkContractId] = useState('');

  useEffect(() => {
    paperlessApi.result(resultId)
      .then(d => { setData(d); setLoading(false); })
      .catch(() => { setError('Fehler beim Laden'); setLoading(false); });
  }, [resultId]);

  useEffect(() => {
    if (!data) return;
    const pf = JSON.parse(data.proposed_fields_json || '{}');
    const init = {};
    Object.keys(pf).forEach(k => { init[k] = true; });
    setSelectedFields(init);
    setNewTitel(data.paperless_title || '');
    if (data.matched_contract_id) setLinkContractId(String(data.matched_contract_id));
  }, [data]);

  async function doAction(action, extra = {}) {
    setActionLoading(true); setActionError(null);
    try {
      await paperlessApi.applyAction(resultId, action, extra);
      onApplied();
      onClose();
    } catch (err) {
      setActionError(err.response?.data?.error || err.message || 'Fehler');
      setActionLoading(false);
    }
  }

  if (loading) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card, #fff)', borderRadius: 12, padding: 40 }}>Lade…</div>
    </div>
  );

  if (error || !data) return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card, #fff)', borderRadius: 12, padding: 40 }}>
        <p style={{ color: '#DC2626' }}>{error}</p>
        <button onClick={onClose}>Schließen</button>
      </div>
    </div>
  );

  const rt = RESULT_TYPE_CONFIG[data.result_type] || { label: data.result_type, icon: '📄', color: '#6B7280', bg: '#F3F4F6' };
  const proposedFields = JSON.parse(data.proposed_fields_json || '{}');
  const extractedFields = JSON.parse(data.extracted_fields_json || '[]');
  const isPending = data.status === 'PENDING';

  const FIELD_LABELS = {
    anbieter: 'Anbieter', vertragsnummer: 'Vertragsnummer', kundennummer: 'Kundennummer',
    vertragsbeginn: 'Vertragsbeginn', vertragsende: 'Vertragsende',
    kosten_monatlich: 'Monatliche Kosten', kosten_jaehrlich: 'Jährliche Kosten',
    kuendigungsfrist: 'Kündigungsfrist', auto_renewal: 'Auto-Renewal',
    zahlungsintervall: 'Zahlungsintervall', telefon: 'Telefon', email: 'E-Mail',
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 200, overflowY: 'auto', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 12px' }}>
      <div style={{ background: 'var(--bg-card, #fff)', borderRadius: 14, width: 780, maxWidth: '98vw', boxShadow: '0 8px 40px rgba(0,0,0,.18)', position: 'relative' }}>
        {/* Header */}
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid #E2E8F0', display: 'flex', gap: 12, alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6 }}>
              <span style={{ background: rt.bg, color: rt.color, borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 }}>{rt.icon} {rt.label}</span>
              <span style={{ background: STATUS_CONFIG[data.status]?.bg || '#F3F4F6', color: STATUS_CONFIG[data.status]?.color || '#6B7280', borderRadius: 20, padding: '3px 10px', fontSize: 12 }}>
                {STATUS_CONFIG[data.status]?.label || data.status}
              </span>
            </div>
            <h2 style={{ margin: 0, fontSize: 17, lineHeight: 1.35 }}>{data.paperless_title || `Dokument #${data.paperless_document_id}`}</h2>
            <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 4 }}>
              Paperless ID {data.paperless_document_id} · Erstellt: {formatDate(data.paperless_created)} · Konfidenz: {konfidenzLabel(data.classification_confidence)}
            </div>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'none', fontSize: 20, cursor: 'pointer', color: '#94A3B8', flexShrink: 0 }}>✕</button>
        </div>

        {/* 3-column body */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 0, padding: '0' }}>
          {/* Col 1: Dokument-Info */}
          <div style={{ padding: '20px 20px', borderRight: '1px solid #F1F5F9' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: .5 }}>Dokument</h3>
            <dl style={{ margin: 0, display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 10px', fontSize: 13 }}>
              <dt style={{ color: '#94A3B8' }}>Typ</dt><dd style={{ margin: 0, fontWeight: 500 }}>{data.document_type || '–'}</dd>
              <dt style={{ color: '#94A3B8' }}>Kategorie</dt><dd style={{ margin: 0 }}>{data.document_category || '–'}</dd>
              <dt style={{ color: '#94A3B8' }}>Erstellt</dt><dd style={{ margin: 0 }}>{formatDate(data.paperless_created)}</dd>
            </dl>
            {data.paperless_url && (
              <a href={data.paperless_url} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', marginTop: 14, fontSize: 12, color: '#6366F1', textDecoration: 'none', fontWeight: 500 }}>
                📄 In Paperless öffnen ↗
              </a>
            )}
          </div>

          {/* Col 2: Erkannte Felder */}
          <div style={{ padding: '20px 20px', borderRight: '1px solid #F1F5F9' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: .5 }}>Erkannte Felder</h3>
            {extractedFields.length === 0 ? (
              <p style={{ fontSize: 13, color: '#94A3B8' }}>Keine Felder erkannt</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {extractedFields.map((f, i) => (
                  <div key={i} style={{ fontSize: 12, background: '#F8FAFC', borderRadius: 6, padding: '5px 8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ color: '#64748B', fontWeight: 600 }}>{FIELD_LABELS[f.feldname] || f.feldname}</span>
                      <span style={{ background: f.quelle === 'FAKT' ? '#ECFDF5' : '#EFF6FF', color: f.quelle === 'FAKT' ? '#059669' : '#2563EB', borderRadius: 10, padding: '1px 6px', fontSize: 11 }}>{f.quelle}</span>
                    </div>
                    <div style={{ fontWeight: 500, color: '#1E293B', marginTop: 1 }}>{String(f.normalisierter_wert ?? f.erkannter_wert)}</div>
                    {f.fundstelle && <div style={{ color: '#94A3B8', fontSize: 11, marginTop: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.fundstelle}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Col 3: Match & Aktionen */}
          <div style={{ padding: '20px 20px' }}>
            <h3 style={{ fontSize: 13, fontWeight: 700, color: '#475569', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: .5 }}>Vertrag</h3>
            {data.matched_contract_id ? (
              <div style={{ background: '#F0FDF4', borderRadius: 8, padding: '10px 12px', marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>Match {data.match_score != null ? `(Score ${Math.round(data.match_score)})` : ''}</div>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{data.matched_contract_titel || `Vertrag #${data.matched_contract_id}`}</div>
                <div style={{ fontSize: 12, color: '#64748B' }}>{data.matched_contract_anbieter}</div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: '#94A3B8', marginBottom: 12 }}>Kein bestehender Vertrag gefunden</div>
            )}

            {actionError && <div style={{ background: '#FEF2F2', color: '#DC2626', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 10 }}>{actionError}</div>}

            {!isPending && (
              <div style={{ background: '#F8FAFC', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: '#475569' }}>
                Aktion: <strong>{data.applied_action}</strong> am {formatDateTime(data.applied_at)}
              </div>
            )}

            {isPending && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {/* Apply fields to existing contract */}
                {data.matched_contract_id && Object.keys(proposedFields).length > 0 && (
                  <div style={{ background: '#0f172a', borderRadius: 8, padding: 12, border: '1px solid #334155' }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#94a3b8' }}>Felder übernehmen</div>
                    {Object.entries(proposedFields).map(([k, v]) => (
                      <label key={k} style={{ display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: 12, marginBottom: 6 }}>
                        <input type="checkbox" checked={!!selectedFields[k]} onChange={e => setSelectedFields(s => ({ ...s, [k]: e.target.checked }))} style={{ marginTop: 3, accentColor: '#818cf8' }} />
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span style={{ color: '#64748b' }}>{FIELD_LABELS[k] || k}:</span>
                            <span style={{ fontWeight: 600, color: '#f1f5f9' }}>{String(v.wert)}</span>
                            <span style={{ fontSize: 10, color: v.quelle === 'FAKT' ? '#34d399' : '#94a3b8' }}>{v.quelle}</span>
                          </div>
                          {v.alter_wert != null && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, marginTop: 4 }}>
                              <div style={{ background: '#1e293b', borderRadius: 4, padding: '4px 8px', border: '1px solid #334155' }}>
                                <div style={{ fontSize: 9, color: '#64748b', textTransform: 'uppercase', marginBottom: 2 }}>Bisher</div>
                                <span style={{ color: '#94a3b8', textDecoration: 'line-through', fontSize: 11 }}>{String(v.alter_wert)}</span>
                              </div>
                              <div style={{ background: '#1e293b', borderRadius: 4, padding: '4px 8px', border: '1px solid #818cf844' }}>
                                <div style={{ fontSize: 9, color: '#818cf8', textTransform: 'uppercase', marginBottom: 2 }}>Neu</div>
                                <span style={{ color: '#f1f5f9', fontWeight: 600, fontSize: 11 }}>{String(v.wert)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      </label>
                    ))}
                    <button disabled={actionLoading} onClick={() => {
                      const fields = {};
                      Object.entries(proposedFields).forEach(([k, v]) => { if (selectedFields[k]) fields[k] = v.wert; });
                      doAction('apply-fields', { data: { contract_id: data.matched_contract_id, fields } });
                    }} style={{ marginTop: 8, width: '100%', padding: '7px 0', borderRadius: 6, background: '#22c55e', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                      Ausgewählte Felder übernehmen
                    </button>
                  </div>
                )}

                {/* Link to contract */}
                <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#334155' }}>Mit Vertrag verknüpfen</div>
                  <select value={linkContractId} onChange={e => setLinkContractId(e.target.value)}
                    style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 12, marginBottom: 8 }}>
                    <option value="">Vertrag wählen…</option>
                    {vertraege.map(v => <option key={v.id} value={v.id}>{v.anbieter ? `${v.anbieter} – ` : ''}{v.titel}</option>)}
                  </select>
                  <button disabled={actionLoading || !linkContractId} onClick={() => doAction('link', { data: { contract_id: parseInt(linkContractId) } })}
                    style={{ width: '100%', padding: '7px 0', borderRadius: 6, background: '#6366F1', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13, opacity: linkContractId ? 1 : .5 }}>
                    Verknüpfen
                  </button>
                </div>

                {/* Create new contract */}
                {(data.result_type === 'NEW_CONTRACT') && (
                  <div style={{ background: '#F8FAFC', borderRadius: 8, padding: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 8, color: '#334155' }}>Neuen Vertrag anlegen</div>
                    {!showCreateForm ? (
                      <button onClick={() => setShowCreateForm(true)} style={{ width: '100%', padding: '7px 0', borderRadius: 6, background: '#7C3AED', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                        Vertrag erstellen…
                      </button>
                    ) : (
                      <>
                        <input value={newTitel} onChange={e => setNewTitel(e.target.value)} placeholder="Vertragsbezeichnung *"
                          style={{ width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid #CBD5E1', fontSize: 12, marginBottom: 8, boxSizing: 'border-box' }} />
                        <button disabled={actionLoading || !newTitel.trim()} onClick={() => doAction('create-contract', { data: { titel: newTitel.trim() } })}
                          style={{ width: '100%', padding: '7px 0', borderRadius: 6, background: '#7C3AED', color: '#fff', border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
                          {actionLoading ? 'Anlegen…' : 'Jetzt anlegen'}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {/* Ignore */}
                <button disabled={actionLoading} onClick={() => doAction('ignore')}
                  style={{ width: '100%', padding: '7px 0', borderRadius: 6, background: 'transparent', color: '#94A3B8', border: '1px solid #CBD5E1', cursor: 'pointer', fontSize: 13 }}>
                  Ignorieren
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Haupt-Seite ──────────────────────────────────────────────────────────────

export default function PaperlessScanPage() {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(true);
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [resultsLoading, setResultsLoading] = useState(false);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanMsg, setScanMsg] = useState(null);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('PENDING');
  const [page, setPage] = useState(1);
  const [showConfig, setShowConfig] = useState(false);
  const [selectedResultId, setSelectedResultId] = useState(null);
  const [vertraege, setVertraege] = useState([]);

  const loadStatus = useCallback(async () => {
    try {
      const s = await paperlessApi.status();
      setStatus(s);
    } catch { setStatus({ configured: false }); }
    finally { setStatusLoading(false); }
  }, []);

  const loadResults = useCallback(async () => {
    setResultsLoading(true);
    try {
      const params = { page, limit: 50 };
      if (filterType) params.result_type = filterType;
      if (filterStatus) params.status = filterStatus;
      const data = await paperlessApi.results(params);
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch { setResults([]); }
    finally { setResultsLoading(false); }
  }, [page, filterType, filterStatus]);

  useEffect(() => { loadStatus(); vertraegeApi.list().then(r => setVertraege(Array.isArray(r?.data) ? r.data : [])).catch(() => {}); }, [loadStatus]);
  useEffect(() => { if (status?.configured) loadResults(); }, [loadResults, status]);

  async function startScan() {
    setScanLoading(true); setScanMsg(null);
    try {
      await paperlessApi.scan();
      setScanMsg({ type: 'ok', text: 'Scan gestartet. Ergebnisse erscheinen in Kürze.' });
      setTimeout(() => { loadStatus(); loadResults(); }, 3000);
    } catch (err) {
      setScanMsg({ type: 'err', text: err.response?.data?.error || 'Scan-Fehler' });
    } finally {
      setScanLoading(false);
    }
  }

  const pendingCount = results.filter(r => r.status === 'PENDING').length;
  const actionableCount = results.filter(r => r.status === 'PENDING' && r.result_type !== 'NOT_CONTRACT_RELEVANT').length;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '16px 16px 80px' }}>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>📄 Paperless Scan</h1>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748B' }}>Dokumente aus Paperless-NGX als Vertragsquellen nutzen</p>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => setShowConfig(true)} style={{ padding: '8px 14px', borderRadius: 8, border: '1px solid #CBD5E1', background: 'transparent', fontSize: 13, cursor: 'pointer' }}>
              ⚙️ Konfigurieren
            </button>
            {status?.configured && (
              <button onClick={startScan} disabled={scanLoading || status?.scan_status === 'running'}
                style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#6366F1', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (scanLoading || status?.scan_status === 'running') ? .6 : 1 }}>
                {scanLoading || status?.scan_status === 'running' ? '⏳ Scan läuft…' : '🔍 Scan starten'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Scan-Nachricht */}
      {scanMsg && (
        <div style={{ padding: '10px 14px', borderRadius: 8, marginBottom: 16, background: scanMsg.type === 'ok' ? '#ECFDF5' : '#FEF2F2', color: scanMsg.type === 'ok' ? '#059669' : '#DC2626', fontSize: 13 }}>
          {scanMsg.text}
        </div>
      )}

      {/* Status-Panel */}
      {!statusLoading && status && (
        <div style={{ background: status.configured ? '#F0FDF4' : '#FFFBEB', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
          {status.configured ? (
            <>
              <div style={{ fontSize: 13 }}>
                <span style={{ color: '#059669', fontWeight: 600 }}>✓ Verbunden</span>
                <span style={{ color: '#64748B', marginLeft: 8 }}>{status.base_url}</span>
              </div>
              {status.document_count != null && (
                <div style={{ fontSize: 13, color: '#475569' }}>📄 {status.document_count} Dokumente</div>
              )}
              {status.last_scan_at && (
                <div style={{ fontSize: 13, color: '#475569' }}>Letzter Scan: {formatDateTime(status.last_scan_at)}</div>
              )}
              {status.scan_status === 'running' && (
                <div style={{ fontSize: 13, color: '#2563EB', fontWeight: 600 }}>⏳ Scan läuft…</div>
              )}
              {actionableCount > 0 && (
                <div style={{ background: '#7C3AED', color: '#fff', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>
                  {actionableCount} ausstehend
                </div>
              )}
            </>
          ) : (
            <div style={{ fontSize: 13, color: '#D97706' }}>⚠️ Paperless noch nicht konfiguriert. Bitte API-Token und URL eingeben.</div>
          )}
        </div>
      )}

      {/* Filter */}
      {status?.configured && (
        <div style={{ marginBottom: 16, display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 12, color: '#64748B', fontWeight: 600 }}>Typ:</span>
          {FILTER_TYPES.map(f => (
            <Chip key={f.value} label={f.label} active={filterType === f.value} onClick={() => { setFilterType(f.value); setPage(1); }} />
          ))}
          <span style={{ fontSize: 12, color: '#64748B', fontWeight: 600, marginLeft: 8 }}>Status:</span>
          {FILTER_STATUS.map(f => (
            <Chip key={f.value} label={f.label} active={filterStatus === f.value} onClick={() => { setFilterStatus(f.value); setPage(1); }} />
          ))}
        </div>
      )}

      {/* Results List */}
      {status?.configured && (
        <>
          {resultsLoading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>Lade…</div>
          ) : results.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#94A3B8' }}>
              {total === 0 ? 'Noch keine Scan-Ergebnisse. Starte einen Scan.' : 'Keine Ergebnisse für diesen Filter.'}
            </div>
          ) : (
            <>
              <div style={{ fontSize: 12, color: '#94A3B8', marginBottom: 10 }}>{total} Ergebnis{total !== 1 ? 'se' : ''}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {results.map(r => {
                  const rt = RESULT_TYPE_CONFIG[r.result_type] || { label: r.result_type, icon: '📄', color: '#6B7280', bg: '#F3F4F6' };
                  const st = STATUS_CONFIG[r.status] || { label: r.status, color: '#6B7280', bg: '#F3F4F6' };
                  return (
                    <div key={r.id} onClick={() => setSelectedResultId(r.id)}
                      style={{ background: 'var(--bg-card, #fff)', border: '1px solid #E2E8F0', borderRadius: 10, padding: '12px 16px', cursor: 'pointer', display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                      <div style={{ fontSize: 20 }}>{rt.icon}</div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {r.paperless_title || `Dokument #${r.paperless_document_id}`}
                        </div>
                        <div style={{ fontSize: 12, color: '#64748B', marginTop: 2 }}>
                          {r.document_type || '–'} · {r.document_category || '–'} · {formatDate(r.paperless_created)}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                        <span style={{ background: rt.bg, color: rt.color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 600 }}>{rt.label}</span>
                        <span style={{ background: st.bg, color: st.color, borderRadius: 20, padding: '2px 10px', fontSize: 11 }}>{st.label}</span>
                        {r.match_score != null && <span style={{ fontSize: 11, color: '#94A3B8' }}>Match {Math.round(r.match_score)}</span>}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Paginierung */}
              {total > 50 && (
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #CBD5E1', cursor: 'pointer', background: 'transparent' }}>← Zurück</button>
                  <span style={{ padding: '6px 12px', fontSize: 13, color: '#475569' }}>Seite {page}</span>
                  <button disabled={results.length < 50} onClick={() => setPage(p => p + 1)}
                    style={{ padding: '6px 14px', borderRadius: 6, border: '1px solid #CBD5E1', cursor: 'pointer', background: 'transparent' }}>Weiter →</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modals */}
      {showConfig && <ConfigModal onClose={() => setShowConfig(false)} onSaved={() => { setShowConfig(false); loadStatus(); }} />}
      {selectedResultId && (
        <DetailModal
          resultId={selectedResultId}
          vertraege={vertraege}
          onClose={() => setSelectedResultId(null)}
          onApplied={() => { loadResults(); loadStatus(); }}
        />
      )}
    </div>
  );
}
